import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';
import { HeadstoneEngravingExample } from '../../components/HeadstoneEngravingExample';

export function QRTab({ memorialId, memorial }) {
  const [info, setInfo] = useState(null);

  // Engraving = manual quote workflow (no Stripe / Printify / commission).
  // Inline form is shown beside the sticker/plaque ordering UI.
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quote, setQuote] = useState({
    deceased_full_name: memorial.full_name || '',
    date_of_birth: memorial.date_of_birth || '',
    date_of_passing: memorial.date_of_passing || '',
    family_name: '',
    cemetery_name: memorial.cemetery_name || '',
    cemetery_address: [memorial.cemetery_address, memorial.cemetery_city, memorial.cemetery_region].filter(Boolean).join(', ') || '',
    cemetery_phone: '',
    stone_material: '',
    preferred_placement: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
  });

  useEffect(() => {
    api.get(`/memorials/${memorialId}/qr-info`).then(({ data }) => setInfo(data));
  }, [memorialId]);

  const submitQuote = async (e) => {
    e.preventDefault();
    setQuoteSubmitting(true);
    try {
      await api.post(`/memorials/${memorialId}/engraving-quote`, quote);
      toast.success('Quote request received — our team will reach out within 2 business days.');
      setQuoteOpen(false);
      setQuote((q) => ({
        ...q,
        family_name: '',
        cemetery_phone: '',
        stone_material: '',
        preferred_placement: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        notes: '',
      }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQuoteSubmitting(false);
    }
  };

  // iter95.7 — QR preview was rendering as a broken-image icon because the
  // <img src=…/api/memorials/{id}/qr?format=png> request is a plain browser
  // GET that can't attach the Authorization: Bearer JWT the endpoint
  // requires. We fetch the QR through the same authenticated `api`
  // instance and render it as a blob URL instead.
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [qrError, setQrError] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    api.get(`/memorials/${memorialId}/qr`, { params: { format: 'png' }, responseType: 'blob' })
      .then(({ data }) => {
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        revoke = url;
        setQrBlobUrl(url);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 402) setQrError('This memorial isn\u2019t activated yet — choose a plan to unlock the QR.');
        else setQrError(formatApiError(e));
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [memorialId]);

  if (!info) return <div className="ml-card">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="ml-card flex flex-col items-center text-center !p-8 plaque-shadow">
        <div className="ml-overline">Forever Here</div>
        <div className="mt-5 w-full max-w-[460px]">
          {qrBlobUrl ? (
            <img
              src={qrBlobUrl}
              alt={`Branded QR plaque for ${memorial.full_name}`}
              className="w-full h-auto"
              data-testid="qr-preview"
            />
          ) : qrError ? (
            <div className="aspect-square flex items-center justify-center bg-[hsl(var(--muted))] rounded-md text-sm text-[hsl(var(--muted-foreground))] p-6" data-testid="qr-preview-error">
              {qrError}
            </div>
          ) : (
            <div className="aspect-square flex items-center justify-center bg-[hsl(var(--muted))] rounded-md text-sm text-[hsl(var(--muted-foreground))]" data-testid="qr-preview-loading">
              Loading QR…
            </div>
          )}
        </div>
        <div className="font-serif text-2xl text-[hsl(var(--primary))] mt-5">{memorial.full_name}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{info.public_url}</div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/memorials/${memorialId}/qr?format=png`} className="ml-btn-primary !py-4 justify-center" data-testid="qr-png-btn">Download PNG</a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/memorials/${memorialId}/qr?format=svg`} className="ml-btn-outline !py-4 justify-center" data-testid="qr-svg-btn">Download SVG</a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/memorials/${memorialId}/qr?format=pdf`} className="ml-btn-gold !py-4 justify-center" data-testid="qr-pdf-btn">Print-ready PDF</a>
      </div>

      {/* Share Kit — one-click download: QR PDF + OG card + caption */}
      <div className="ml-card border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="ml-overline">Share kit</div>
          <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">Everything you need to share {memorial.full_name}</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            A single zip — QR PDF, social share card, and a ready-to-paste caption.
          </div>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/memorials/${memorialId}/share-kit`}
          className="ml-btn-primary !py-3 !px-6 shrink-0"
          data-testid="share-kit-download-btn"
        >
          Download share kit
        </a>
      </div>

      <div className="ml-card flex items-center justify-between">
        <div>
          <div className="ml-overline">Public memorial URL</div>
          <div className="font-mono text-sm mt-1 text-[hsl(var(--primary))] break-all">{info.public_url}</div>
        </div>
        <button onClick={() => navigator.clipboard.writeText(info.public_url)} className="ml-btn-outline shrink-0" data-testid="copy-url-btn">Copy</button>
      </div>

      <div className="ml-card grid sm:grid-cols-3 gap-4 text-center">
        <div><div className="ml-overline">Scans</div><div className="font-serif text-3xl mt-1 text-[hsl(var(--primary))]">{info.scan_count}</div></div>
        <div><div className="ml-overline">Error correction</div><div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">High (30%)</div></div>
        <div><div className="ml-overline">Format</div><div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">Deep green & cream</div></div>
      </div>

      {/* HEADSTONE ENGRAVING — manual quote (no price). */}
      <div className="ml-card" data-testid="engraving-quote-section">
        <div className="grid md:grid-cols-[1fr_320px] gap-6 items-start">
          <div>
            <div className="ml-overline">Headstone Engraving</div>
            <h3 className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">Permanent QR engraving on a headstone</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xl">
              Engraving is coordinated manually with your cemetery, funeral home, or stone vendor.
              Send us the details and our team will reach out within two business days with a quote.
            </p>
          </div>
          <figure
            className="rounded-md overflow-hidden border border-[hsl(var(--primary)/0.12)] bg-[#1a1a1a] shadow-sm"
            data-testid="engraving-example-figure"
          >
            <HeadstoneEngravingExample className="w-full h-auto block object-cover" />
            <figcaption className="text-[10px] tracking-[0.18em] uppercase font-bold text-[hsl(var(--primary))] text-center py-2 px-3 bg-white/70">
              Example · MyLeaf engraved headstone
            </figcaption>
          </figure>
        </div>

        {!quoteOpen ? (
          <button
            type="button"
            onClick={() => setQuoteOpen(true)}
            className="ml-btn-gold mt-5"
            data-testid="request-engraving-quote-btn"
          >
            Request a Quote
          </button>
        ) : (
          <form onSubmit={submitQuote} className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="engraving-quote-form">
            <div className="sm:col-span-2 text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
              Deceased
            </div>
            <input
              required
              placeholder="Full name of the deceased"
              className="ml-input sm:col-span-2"
              value={quote.deceased_full_name}
              onChange={(e) => setQuote({ ...quote, deceased_full_name: e.target.value })}
              data-testid="quote-deceased-name"
            />
            <input
              type="date"
              placeholder="Date of birth"
              className="ml-input"
              value={quote.date_of_birth || ''}
              onChange={(e) => setQuote({ ...quote, date_of_birth: e.target.value })}
              data-testid="quote-deceased-dob"
            />
            <input
              type="date"
              placeholder="Date of passing"
              className="ml-input"
              value={quote.date_of_passing || ''}
              onChange={(e) => setQuote({ ...quote, date_of_passing: e.target.value })}
              data-testid="quote-deceased-dod"
            />

            <div className="sm:col-span-2 mt-2 text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
              Family
            </div>
            <input
              required
              placeholder="Family name"
              className="ml-input sm:col-span-2"
              value={quote.family_name}
              onChange={(e) => setQuote({ ...quote, family_name: e.target.value })}
              data-testid="quote-family-name"
            />

            <div className="sm:col-span-2 mt-2 text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
              Cemetery
            </div>
            <input
              required
              placeholder="Cemetery name"
              className="ml-input sm:col-span-2"
              value={quote.cemetery_name}
              onChange={(e) => setQuote({ ...quote, cemetery_name: e.target.value })}
              data-testid="quote-cemetery-name"
            />
            <input
              required
              placeholder="Cemetery address"
              className="ml-input sm:col-span-2"
              value={quote.cemetery_address}
              onChange={(e) => setQuote({ ...quote, cemetery_address: e.target.value })}
              data-testid="quote-cemetery-address"
            />
            <input
              placeholder="Cemetery phone (if known)"
              className="ml-input"
              value={quote.cemetery_phone}
              onChange={(e) => setQuote({ ...quote, cemetery_phone: e.target.value })}
              data-testid="quote-cemetery-phone"
            />
            <select
              className="ml-input"
              value={quote.stone_material}
              onChange={(e) => setQuote({ ...quote, stone_material: e.target.value })}
              data-testid="quote-stone-material"
            >
              <option value="">Stone material (optional)</option>
              <option value="granite">Granite</option>
              <option value="marble">Marble</option>
              <option value="limestone">Limestone</option>
              <option value="bronze">Bronze</option>
              <option value="other">Other</option>
            </select>
            <input
              placeholder="Preferred placement (optional)"
              className="ml-input sm:col-span-2"
              value={quote.preferred_placement}
              onChange={(e) => setQuote({ ...quote, preferred_placement: e.target.value })}
              data-testid="quote-preferred-placement"
            />

            <div className="sm:col-span-2 mt-2 text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
              Family contact
            </div>
            <input
              required
              placeholder="Your name"
              className="ml-input"
              value={quote.contact_name}
              onChange={(e) => setQuote({ ...quote, contact_name: e.target.value })}
              data-testid="quote-contact-name"
            />
            <input
              required
              type="email"
              placeholder="Your email"
              className="ml-input"
              value={quote.contact_email}
              onChange={(e) => setQuote({ ...quote, contact_email: e.target.value })}
              data-testid="quote-contact-email"
            />
            <input
              placeholder="Your phone (optional)"
              className="ml-input sm:col-span-2"
              value={quote.contact_phone}
              onChange={(e) => setQuote({ ...quote, contact_phone: e.target.value })}
              data-testid="quote-contact-phone"
            />

            <textarea
              rows={3}
              placeholder="Notes / special requests (optional)"
              className="ml-input sm:col-span-2"
              value={quote.notes}
              onChange={(e) => setQuote({ ...quote, notes: e.target.value })}
              data-testid="quote-notes"
            />

            <div className="sm:col-span-2 flex flex-wrap gap-3 mt-2">
              <button
                type="submit"
                disabled={quoteSubmitting}
                className="ml-btn-primary"
                data-testid="submit-engraving-quote-btn"
              >
                {quoteSubmitting ? 'Sending…' : 'Send quote request'}
              </button>
              <button
                type="button"
                onClick={() => setQuoteOpen(false)}
                className="ml-btn-outline"
                data-testid="cancel-engraving-quote-btn"
              >
                Cancel
              </button>
            </div>
            <p className="sm:col-span-2 text-[11px] text-[hsl(var(--muted-foreground))]">
              Your request is stored privately. No charge is made — our team will reach out by email or phone with a quote.
            </p>
          </form>
        )}
      </div>

      {/* ORDER QR MEMORIAL — paid checkout only (iter99). The legacy
          no-charge request form was removed: every physical product now
          goes through Stripe payment BEFORE any order is created. */}
      <div className="ml-card" data-testid="qr-order-section">
        <div className="ml-overline">Order QR Memorial</div>
        <h3 className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">Physical plaque or sticker</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
          Order an acrylic or stainless steel QR plaque for your loved one's
          resting place. Checkout is secure via Stripe — your order enters
          production the moment payment completes.
        </p>
        <div className="mt-5">
          <Link
            to={`/memorials/${memorialId}/order-plaque`}
            className="ml-btn-gold inline-block"
            data-testid="order-plaque-cta"
          >
            Order a plaque — secure checkout →
          </Link>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">
            You'll choose your product, quantity, customization and shipping,
            then pay with Stripe. No order is created until payment succeeds.
          </p>
        </div>
      </div>
    </div>
  );
}
